const STRUCTURED_TOOLS = new Set([
  'coordinate',
  'table',
  'mapping',
  'numberline',
  'polynomialGraph',
  'rationalGraph',
  'trigGraph',
  'multiline',
]);

const nonBlank = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const firstStoredValue = (answer) => (Array.isArray(answer) ? answer[0] : answer);

const parseStructuredAnswer = (answer) => {
  const value = firstStoredValue(answer);
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string' || value.trim() === '') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const hasObjectContent = (value) => {
  if (Array.isArray(value)) return value.some(hasObjectContent);
  if (value && typeof value === 'object') return Object.values(value).some(hasObjectContent);
  return nonBlank(value);
};

const hasEditableTableValue = (parsed, config) => {
  if (!Array.isArray(parsed?.rows)) return false;
  const configRows = Array.isArray(config?.rows) ? config.rows : [];
  const configById = new Map(configRows.map((row) => [String(row.id), row]));

  return parsed.rows.some((row) => {
    if (!row?.cells || typeof row.cells !== 'object') return false;
    const configRow = configById.get(String(row.id));
    return Object.entries(row.cells).some(([columnId, value]) => {
      const configCell = configRow?.cells?.[columnId];
      if (configCell?.prefilled === true) return false;
      return nonBlank(value);
    });
  });
};

const hasGraphAnswer = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return false;
  if (Number.isInteger(parsed?.step2?.selectedGraphIndex)) return true;
  return hasObjectContent(parsed.step1);
};

export const hasMeaningfulHomeworkAnswer = (question, hwAnswer, fileState) => {
  if (question?.question_type === 'file_upload') {
    return Boolean(fileState?.file || fileState?.prev_file);
  }

  const answer = hwAnswer?.answer;
  const tool = question?.answer_tool;

  if (!STRUCTURED_TOOLS.has(tool)) {
    const value = firstStoredValue(answer);
    if (tool === 'mathlive' && typeof value === 'string') {
      try {
        const envelope = JSON.parse(value);
        if (envelope && typeof envelope === 'object' && 'value' in envelope) {
          return nonBlank(envelope.value);
        }
      } catch (_error) {
        // Legacy MathLive answers are plain LaTeX strings.
      }
    }
    return nonBlank(value);
  }

  const rawValue = firstStoredValue(answer);
  const parsed = parseStructuredAnswer(answer);
  // Questions can be switched from a scalar tool to a structured tool after a
  // student answered. Nonblank legacy content is still an attempted answer;
  // the grader may route it to internal review as legacy_format.
  if (!parsed) return nonBlank(rawValue);

  switch (tool) {
    case 'coordinate':
    case 'numberline':
      return Array.isArray(parsed.elements) && parsed.elements.length > 0;
    case 'mapping':
      return Array.isArray(parsed.connections) && parsed.connections.length > 0;
    case 'table':
      return hasEditableTableValue(parsed, question?.answer_config);
    case 'multiline':
      return Array.isArray(parsed.rows) && parsed.rows.some((row) => nonBlank(row?.value));
    case 'polynomialGraph':
    case 'rationalGraph':
    case 'trigGraph':
      return hasGraphAnswer(parsed);
    default:
      return false;
  }
};

export const countMeaningfulHomeworkAnswers = (questions, answersByQuestion, filesByQuestion = {}) =>
  (questions || []).reduce((count, question) => (
    count + (hasMeaningfulHomeworkAnswer(
      question,
      answersByQuestion?.[question.id],
      filesByQuestion?.[question.id]
    ) ? 1 : 0)
  ), 0);

export const normalizeHomeworkAnswerForSave = (question, answer) => (
  hasMeaningfulHomeworkAnswer(question, { answer }, null) ? answer : null
);
